/**
 * RAM — Close Account (Wave 10 dedicated dialog).
 *
 * POST /v3/maint/acct/close (via client.retail.maintActions.submitClose).
 *
 * Replaces the Wave10ActionFallback for `close-account`, which hardcoded
 * `mode: 'graceful'` and made force-close unreachable. This dialog exposes
 * the mode radio so Compliance + Manager can drive a force close when
 * fraud, legal mandate, or charge-off requires it.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import type { RamTarget } from '../types';

export interface CloseAccountProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

type CloseMode = 'graceful' | 'force';

export default function CloseAccount({
  target,
  onDone,
  onCancel,
}: CloseAccountProps): JSX.Element {
  const client = useApiClient();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [mode, setMode] = useState<CloseMode>('graceful');
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const reasonTrimmed = reason.trim();
  const canSave =
    !submitting && Boolean(target.acctId) && reasonTrimmed.length >= 15;

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitClose(b),
      {
        accountNumber: target.acctId,
        mode,
        reason: reasonTrimmed || undefined,
      },
    );
    if (!result.success) return;
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'approver'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          {mode === 'force' ? 'Force-close' : 'Graceful close'} submitted for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  const bannerCopy =
    mode === 'force'
      ? 'Force close: Compliance + Manager approval required. Used for fraud, legal mandate, or charge-off. Documents the close-out reason for audit.'
      : 'Graceful close: Manager approval required. Customer must zero the balance + remit any open items before close completes.';

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Close account</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">{bannerCopy}</Banner>

      <div className="ram-wizard__fields">
        <fieldset className="ram-search__label">
          <legend>Close mode</legend>
          <label>
            <input
              type="radio"
              name="close-mode"
              value="graceful"
              checked={mode === 'graceful'}
              onChange={() => setMode('graceful')}
            />
            {' '}Graceful (Manager approval)
          </label>
          <label>
            <input
              type="radio"
              name="close-mode"
              value="force"
              checked={mode === 'force'}
              onChange={() => setMode('force')}
            />
            {' '}Force (Compliance + Manager approval)
          </label>
        </fieldset>
        <label className="ram-search__label">
          Reason (min 15 characters)
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={512}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : mode === 'force' ? 'Submit force close' : 'Submit graceful close'}
        </Button>
      </div>
    </div>
  );
}
